import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import BillingStatement from '@/components/tenant/BillingStatement';
import PaymentResultModal from '@/components/tenant/PaymentResultModal';
import { usePayments } from '@/hooks/usePayments';
import { useToast } from '@/components/shared/ToastProvider';
import { CreatePaymentRequest, PaymentOptions } from '@/types/payments';
import { profileSelector } from '@/store/profile/profileSlices';
import { getPaymentRequest } from '@/api';
import './PaymentsPage.scss';


interface PaymentResultState {
  isOpen: boolean;
  status: 'success' | 'failure' | 'cancelled' | 'error';
  paymentId?: string;
  amount?: number;
  message?: string;
}

const PaymentsPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { showToast } = useToast();
  const { createPayment, testConnection, loading, error, clearError } = usePayments();
  const profile = useSelector(profileSelector);

  const [isProcessing, setIsProcessing] = useState(false);
  // Генеруємо випадкову суму одразу при ініціалізації компонента
  const [currentPaymentAmount] = useState<number>(() => {
    return Number((Math.random() * (1500 - 1000) + 1000).toFixed(2));
  });
  const [paymentResult, setPaymentResult] = useState<PaymentResultState>({
    isOpen: false,
    status: 'success'
  });

  // Перевірка статусу платежу при поверненні з Express
  useEffect(() => {
    const paymentId = searchParams.get('id');
    const status = searchParams.get('status');
    const amount = searchParams.get('amount');

    if (paymentId && status) {
      const parsedAmount = amount ? parseFloat(amount) : undefined;

      switch (status) {
        case 'success':
          setPaymentResult({
            isOpen: true,
            status: 'success',
            paymentId,
            amount: parsedAmount,
            message: 'Your payment has been processed successfully! 🎉'
          });
          break;
        case 'failure':
          setPaymentResult({
            isOpen: true,
            status: 'failure',
            paymentId,
            amount: parsedAmount,
            message: 'Payment failed. Please try again.'
          });
          break;
        case 'cancelled':
          setPaymentResult({
            isOpen: true,
            status: 'cancelled',
            paymentId,
            amount: parsedAmount,
            message: 'Payment was cancelled by user.'
          });
          break;
        case 'error':
          setPaymentResult({
            isOpen: true,
            status: 'error',
            paymentId,
            amount: parsedAmount,
            message: 'An error occurred during payment processing.'
          });
          break;
        default:
          setPaymentResult({
            isOpen: true,
            status: 'error',
            paymentId,
            amount: parsedAmount,
            message: 'Payment status unknown.'
          });
      }

      // Очищаємо URL параметри після відкриття модалки
      navigate('/payments', { replace: true });
    }
  }, [searchParams, navigate]);

  // Показ помилок через Toast (тільки для системних помилок)
  useEffect(() => {
    if (error) {
      showToast(error, 'error');
      clearError();
    }
  }, [error, showToast, clearError]);

  const handleClosePaymentResult = () => {
    setPaymentResult(prev => ({ ...prev, isOpen: false }));
  };

  const handleViewPaymentDetails = async (paymentId: string) => {
    try {
      const response = await getPaymentRequest(paymentId);
      return response.data.data;
    } catch (error) {
      console.error('Failed to fetch payment details:', error);
      throw error;
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleExportPDF = () => {
    showToast('PDF export functionality coming soon!', 'info');
    // TODO: Implement PDF export functionality
  };

  const handlePayment = async (
    amount: number,
    paymentMethodId: string,
    options: PaymentOptions
  ) => {
    // Валідація перед відправкою
    if (!options.agreeToTerms) {
      showToast('Please agree to Terms of Service and Late Fee Policy', 'error');
      return;
    }

    setIsProcessing(true);

    try {
      // Використовуємо згенеровану при завантаженні суму
      const paymentAmount = currentPaymentAmount;

      // Отримуємо реальну інформацію з профілю
      const customerName = profile
        ? `${profile.firstname || ''} ${profile.lastname || ''}`.trim()
        : 'Test Customer';
      const customerEmail = profile?.email || 'test@example.com';

      // Формуємо дані для створення платежу
      const paymentData: CreatePaymentRequest = {
        amount: paymentAmount,
        tenantId: 1,
        currency: 'USD',
        type: 'rent',
        description: `Payment of ${paymentAmount.toFixed(2)} - Testing`,
        // customerName,
        // customerEmail,

        // Додаткові дані
        metadata: {
          paymentMethodId,
          recurringPayment: options.isRecurring,
          receiptEmail: options.receiptDelivery.email,
          receiptSMS: options.receiptDelivery.sms,
          authorizeStorage: options.authorizeStorage,
          source: 'billing_statement',
          originalAmount: amount,
          isTestPayment: true,
        },
      };

      console.log('🔍 PAYMENT DATA:', {
        ...paymentData,
        note: `Using generated amount: ${paymentAmount} (original UI amount: ${amount})`
      });

      // Створюємо платіж через API
      const paymentResponse = await createPayment(paymentData);

      if (paymentResponse && paymentResponse.hostedPaymentUrl) {
        showToast(`Redirecting to payment page... (Amount: ${paymentAmount})`, 'info');

        // Невелика затримка для показу повідомлення, потім перенаправлення
        setTimeout(() => {
          window.location.href = paymentResponse.hostedPaymentUrl;
        }, 1500);
      } else {
        throw new Error('Payment URL not received from server');
      }
    } catch (err: any) {
      console.error('Payment creation failed:', err);
      showToast(
        err.message || 'Failed to create payment. Please try again.',
        'error'
      );
    } finally {
      setIsProcessing(false);
    }
  };

  // Тестова функція для перевірки з'єднання з Express API
  const handleTestConnection = async () => {
    try {
      const isConnected = await testConnection();
      if (isConnected) {
        showToast('Payment system connection successful!', 'success');
      } else {
        showToast('Payment system connection failed!', 'error');
      }
    } catch (err) {
      showToast('Connection test failed!', 'error');
    }
  };

  return (
    <div className="PaymentsPage">
      {/* Лоадер під час обробки платежу */}
      {(loading || isProcessing) && (
        <div className="PaymentsPage__loading">
          <div className="PaymentsPage__spinner"></div>
          <p>
            {isProcessing
              ? 'Processing payment...'
              : loading
                ? 'Loading...'
                : 'Please wait...'
            }
          </p>
        </div>
      )}

      {      /* Основний компонент з формою платежу */}
      <BillingStatement
        onBack={handleBack}
        onExportPDF={handleExportPDF}
        onPayment={handlePayment}
        overrideAmount={currentPaymentAmount}
      />

      {/* Модальне вікно з результатами платежу */}
      <PaymentResultModal
        isOpen={paymentResult.isOpen}
        onClose={handleClosePaymentResult}
        status={paymentResult.status}
        paymentId={paymentResult.paymentId}
        amount={paymentResult.amount}
        message={paymentResult.message}
        onViewDetails={handleViewPaymentDetails}
      />

      {/* Development tools - показуються тільки в режимі розробки */}
      {process.env.NODE_ENV === 'development' && (
        <div className="PaymentsPage__dev-tools">
          <button
            onClick={handleTestConnection}
            className="PaymentsPage__test-button"
            disabled={loading || isProcessing}
          >
            Test Connection
          </button>
        </div>
      )}
    </div>
  );
};

export default PaymentsPage;